import type { ComponentProps, FunctionComponent, ReactNode } from "react";
import type { IconProps } from "@tamagui/helpers-icon";
import { Wrench as WrenchIcon } from "@tamagui/lucide-icons";
import { BackgroundRadialGrantedGradient, Body1, Button, Circle, isWeb, ScrollView, Title2, YStack } from "@medbillai/ui";
import type { UserFacingErrorScreenReport } from "../lib/sentry/reportUserFacingErrorScreen";
import { ReportUserFacingErrorScreen } from "./ReportUserFacingErrorScreen";
import { ShadowLoginBanner } from "./ShadowLoginBanner";
import { gradientBackgroundCircleScreenPadding } from "./screenPadding";
type ButtonProps = ComponentProps<typeof Button>;
export type ErrorScreenAction = {
  label: string;
  onPress: NonNullable<ButtonProps["onPress"]>;
} & Partial<Pick<ButtonProps, "href" | "accessibilityRole" | "disabled">>;
export type ErrorScreenProps = {
  title: string;
  message?: string;
  children?: ReactNode;
  action?: ErrorScreenAction;
  icon?: FunctionComponent<IconProps>;
  // The shadow banner logs out through the router; a crash tree has no
  // router provider above it.
  withoutShadowBanner?: boolean;
  report?: UserFacingErrorScreenReport;
};
const ErrorScreenActionButton = ({
  action
}: {
  action: ErrorScreenAction;
}) => {
  const {
    label,
    ...buttonProps
  } = action;
  return <Button variant="primary" size="large" {...buttonProps} data-sentry-element="Button" data-sentry-component="ErrorScreenActionButton" data-sentry-source-file="ErrorScreen.tsx">
      {label}
    </Button>;
};
export function ErrorScreen({
  title,
  message,
  children,
  action,
  icon: Icon = WrenchIcon,
  withoutShadowBanner = false,
  report
}: ErrorScreenProps) {
  const screen = <YStack flex={1} width="100%" backgroundColor="$background">
      <BackgroundRadialGrantedGradient />
      {!isWeb && !withoutShadowBanner ? <YStack fg={0}>
          <ShadowLoginBanner />
        </YStack> : null}
      <ScrollView flex={1} width="100%" keyboardShouldPersistTaps="handled" contentContainerStyle={{
      flexGrow: 1,
      justifyContent: "center"
    }}>
        <YStack width="100%" maxWidth={600} alignSelf="center" alignItems="center" gap="$6" px="$8" pt={gradientBackgroundCircleScreenPadding} pb="$4" $short={{
        gap: "$2",
        px: "$4"
      }}>
          <Circle size="$8" backgroundColor="$background" justifyContent="center" alignItems="center">
            <Icon size={36} strokeWidth={2} color="$iconDefault" />
          </Circle>
          <Title2 textAlign="center">{title}</Title2>
          <YStack px="$4" gap="$4" width="100%">
            {children ?? <Body1 color="$textSubtle" textAlign="center">
                {message}
              </Body1>}
          </YStack>
        </YStack>
      </ScrollView>
      {action ? <YStack width="100%" maxWidth={600} alignSelf="center" px="$8" pt="$3" $short={{
      px: "$4"
    }} $platform-web={{
      pb: "$4.5"
    }}>
          <ErrorScreenActionButton action={action} />
        </YStack> : null}
    </YStack>;
  return report ? <ReportUserFacingErrorScreen {...report} data-sentry-element="ReportUserFacingErrorScreen" data-sentry-component="ErrorScreen" data-sentry-source-file="ErrorScreen.tsx">
      {screen}
    </ReportUserFacingErrorScreen> : screen;
}